/* eslint-disable camelcase */

import { createAction, handleActions } from 'redux-actions';
import { getDataProvider } from '../../data-providers';

export const createUser = createAction('CREATE_USER', (user) => {
  return getDataProvider().createAndLoginUser(user);
});

export const updateUser = createAction('UPDATE_USER', (payload) => {
  return getDataProvider().updateUser(payload);
});

export const setUserAuthCredentials = createAction('SET_USER_AUTH', () => {
  return getDataProvider().setUserAuthCredentials();
});

export const acceptToS = createAction('ACCEPT_TOS', () => {
  return getDataProvider().acceptToS();
});

export const loginWithEmail = createAction('LOGIN_WITH_EMAIL', (username, password) => {
  return getDataProvider().loginAndGetUser({
    grant_type: 'password',
    username,
    password
  });
});

export const loginWithFacebook = createAction('LOGIN_WITH_FACEBOOK', (accessToken) => {
  return getDataProvider().loginAndGetUser({
    grant_type: 'external',
    provider: 'facebook',
    provider_token: accessToken
  });
});

export const connectFacebook = createAction('CONNECT_FACEBOOK', (facebookToken) => {
  return getDataProvider().connectFacebook(facebookToken);
});

export const loginViaPlanetFitnessSSO = createAction('LOGIN_VIA_PF_SSO', (code) => {
  return getDataProvider()
    .loginViaPlanetFitnessSSO(code);
});

export const loginUsingPasswordWithPlanetFitness = createAction('LOGIN_PASSWORD_PF', ({ email, password }) => {
  return getDataProvider()
    .loginWithPFUsingPassword({
      email,
      password
    });
});

export const loginWithPlanetFitness = createAction('LOGIN_WITH_PF', ({ access_token, expires_in, id_token, refresh_token }) => {
  return getDataProvider()
    .loginWithPlanetFitness({
      access_token,
      expires_in,
      id_token,
      refresh_token
    });
});

export const getPlanetFitnessAuth = createAction('GET_PF_AUTH', () => {
  return getDataProvider().getPlanetFitnessAuthFromNative();
});

export const getLoginWithApple = createAction('GET_APPLE_LOGIN', () => {
  return getDataProvider().getAppleLoginFromNative();
});

export const sendResetPasswordEmail = createAction('RESET_PASSWORD', (email) => {
  return getDataProvider().sendResetPasswordEmail(email);
});

export const getUserProfile = createAction('GET_USER_PROFILE', () => {
  return getDataProvider().getUserProfile();
});

export const loadUserEmail = createAction('LOAD_USER_EMAIL', (email) => {
  return Promise.resolve(email);
});

export const updateUserPreferences = createAction('UPDATE_USER_PREFERENCES', (payload) => {
  return getDataProvider().updateUserPreferences(payload);
});

export const onboardingCompleted = createAction('ONBOARDING_COMPLETED', (connectedProduct) => {
  return getDataProvider().onboardingCompleted({ connectedProduct });
});

export const checkEmailExists = createAction('CHECK_EMAIL_EXISTS', (email) => {
  return getDataProvider().checkEmailExists(email);
});

export const updateEmailWarning = createAction('UPDATE_EMAIL_WARNING', (warning) => {
  return Promise.resolve(warning);
});

export const saveActivityLevel = activityLevel => updateUser({ activity_level: activityLevel });

export const saveHeight = (height, unitSystem) => updateUser({ height: height / 100, unit_system: unitSystem });

export const saveGender = gender => updateUser({ gender });

export const saveCurrentWeight = (weight, unitSystem) => updateUser({ weight, unit_system: unitSystem });

export const saveGoalWeight = (weight, unitSystem) => updateUser({ goal_weight: weight, unit_system: unitSystem });

export const saveWorkoutDays = workoutDays => updateUser({ workout_days: workoutDays });

export const saveEquipmentPreferences = equipment => updateUserPreferences({ equipment });

export const saveFitnessGoal = fitnessGoal => updateUserPreferences({ fitness_goal: fitnessGoal });

export const saveFitnessLevel = fitnessLevel => updateUserPreferences({ fitness_level: fitnessLevel });

export const saveMultiFitnessGoals = fitnessGoals => updateUserPreferences({ multiple_fitness_goals: fitnessGoals });

export const saveWorkoutLength = (workoutLength) => {
  return updateUser({
    preferred_workout_length_min: workoutLength.min,
    preferred_workout_length_max: workoutLength.max
  });
};

export const saveWeightGoal = weightGoal => updateUser({ target_weight_change: weightGoal });

export const saveWorkoutTypes = workoutTypes => updateUserPreferences({ workout_types: workoutTypes });

export const savePerformanceAreas = performanceAreas => updateUserPreferences({ performance_goals: performanceAreas });

export const saveMuscleGroups = muscleGroups => updateUserPreferences({ target_areas: muscleGroups });

export const saveBirthday = birthday => updateUser({ birthday });

export default handleActions({
  [`${loginWithEmail}_PENDING`]: (state, action) => {
    return { pending: true, done: false, error: null };
  },
  [`${loginWithEmail}_FULFILLED`]: (state, action) => {
    return { pending: false, done: true, error: null, ...action.payload };
  },
  [`${loginWithEmail}_REJECTED`]: (state, action) => {
    return { pending: false, done: true, error: action.payload };
  },
  [`${loginWithFacebook}_PENDING`]: (state, action) => {
    return { pending: true, done: false };
  },
  [`${loginWithFacebook}_FULFILLED`]: (state, action) => {
    return { pending: false, done: true, ...action.payload };
  },
  [`${loginWithFacebook}_REJECTED`]: (state, action) => {
    return { pending: false, done: true };
  },
  [`${createUser}_FULFILLED`]: (state, action) => {
    return { ...action.payload };
  },
  [`${updateUser}_FULFILLED`]: (state, action) => {
    return Object.assign({}, state, action.payload);
  },
  [`${updateUserPreferences}_FULFILLED`]: (state, action) => {
    return Object.assign({}, state, action.payload);
  },
  [`${getUserProfile}_FULFILLED`]: (state, action) => {
    return { ...action.payload };
  },
  [`${loadUserEmail}_FULFILLED`]: (state, action) => {
    return Object.assign({}, state, { email: action.payload });
  },
  [`${checkEmailExists}_FULFILLED`]: (state, action) => {
    return Object.assign({}, state, { emailWarning: action.payload });
  },
  [`${updateEmailWarning}_FULFILLED`]: (state, action) => {
    return Object.assign({}, state, { emailWarning: action.payload });
  },
}, { pending: false, done: false, error: null });
